import {
  getSessionCourtNumbers,
  getSessionTimeRangeInfo,
  getSessionCourtRange,
  formatSessionRangeText,
  formatSessionDateLabel,
} from '../utils/openPlayUtil';
import { formatTimeSlotRange, getCourtLabel } from '../utils/bookingUtils';
import { OPEN_PLAY_COURTS_INLINE_MAX_CHARS } from '../constants/bookingConst';
import { linkifyText } from '../utils/linkify';
import { formatMoney } from '../utils/currencyUtil';
import '../css/UserInfoForm.css';
import '../css/OpenPlayModal.css';

/**
 * The session's own details, rendered identically on the join screen and on
 * the payment screen so the guest is looking at the same summary they agreed
 * to. `showNotes` is off for payment: the notes field is the admin's
 * RSVP/marketing blurb, which is context for deciding to join and noise once
 * the guest is being asked to pay.
 *
 * Shared by OpenPlayModal (join form, confirmation) and
 * OpenPlayCheckoutSummary (the checkout tab).
 *
 * @param {Object} props
 * @param {Object|null} props.session - the hosted openPlaySessions record, or null for a plain Open Play cell.
 * @param {Object} props.config - club config (court labels, currency).
 * @param {string} props.sportName
 * @param {string|null} props.dateStr - "yyyy-MM-dd", used when there is no session.
 * @param {string|null} props.timeSlot - the clicked cell's slot, used when there is no session.
 * @param {number} props.pricePerPlayer
 * @param {boolean} props.showNotes - whether to show the session's notes.
 * @returns {JSX.Element}
 */
function OpenPlaySessionDetails({ session, config, sportName, dateStr, timeSlot, pricePerPlayer, showNotes }) {
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (n) => formatMoney(n, config);

  // Falls back to the single clicked cell when this Open Play slot has no
  // hosted session behind it (a plain per-cell "Open Play" status set from
  // the admin grid) — there is no court/time range to summarise then.
  if (!session) {
    return (
      <div className="uif-booking-box opm-details">
        <h3>{sportName} Open Play</h3>
        <div className="opm-detail-line">
          {[dateStr ? formatSessionDateLabel(dateStr) : '', timeSlot ? formatTimeSlotRange(timeSlot) : '']
            .filter(Boolean).join(' · ')}
        </div>
        <div className="opm-detail-line">{peso(pricePerPlayer)} / player</div>
      </div>
    );
  }

  const courts = getSessionCourtNumbers(session);
  const { startHour, endHour, variesByCourt, endsNextDay } = getSessionTimeRangeInfo(session);
  const courtLabels = courts.map(c => getCourtLabel(config, c));
  const courtsText = courtLabels.join(', ');
  const timeRangeText = startHour === null
    ? ''
    : formatSessionRangeText(startHour, endHour, endsNextDay);
  // Roughly what fits after a time range on one line in this box. Erring
  // toward a second line is the safe direction: a court list alone on its
  // own line always reads correctly, a wrapped one doesn't.
  const courtsNeedOwnLine = courtsText.length > OPEN_PLAY_COURTS_INLINE_MAX_CHARS;

  return (
    <div className="uif-booking-box opm-details">
      <h3>{session.sportName || sportName} Open Play</h3>
      <div className="opm-detail-line">{formatSessionDateLabel(session.date)}</div>

      {/* Each court can carry its own hours (AdminHostOpenPlayModal's
          per-court time picker), so a single "1:00 PM – 7:00 PM" line would
          be a lie for a session where they differ — those get one line each
          instead. */}
      {variesByCourt ? (
        courts.map((court) => {
          // Via getSessionCourtRange rather than reading `courts` directly:
          // an overnight session keeps its after-midnight hours in a second
          // map, and a court read straight off `courts` would stop at
          // midnight and quote the guest a shorter session than they get.
          const range = getSessionCourtRange(session, court);
          if (!range) return null;
          return (
            <div className="opm-detail-line" key={court}>
              {getCourtLabel(config, court)}: {formatSessionRangeText(range.startHour, range.endHour, range.endsNextDay)}
            </div>
          );
        })
      ) : startHour !== null && (
        // A club that names its courts ("Court, Training Balls (Set)")
        // overruns the time line and wraps mid-name, which reads as two
        // unrelated fragments. Past a threshold the courts get their own
        // line instead — decided on the text, not on a CSS wrap we can't
        // detect, so it behaves the same at every width.
        courtsNeedOwnLine ? (
          <>
            <div className="opm-detail-line">{timeRangeText}</div>
            <div className="opm-detail-line opm-detail-courts">{courtsText}</div>
          </>
        ) : (
          <div className="opm-detail-line">{timeRangeText} · {courtsText}</div>
        )
      )}

      <div className="opm-detail-line">{peso(pricePerPlayer)} / player</div>
      {/* Notes are admin free text and routinely carry an RSVP link. As
          plain text a guest had to select and copy it by hand — usually
          impossible on a phone. */}
      {showNotes && session.notes && <p className="opm-notes">{linkifyText(session.notes)}</p>}
    </div>
  );
}

export default OpenPlaySessionDetails;
